/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Stack } from "@mui/material";
import { HeatXScrapYTable } from "./HeatXScrapYTable";
import { ScrapXHeatYTable } from "./ScrapXHeatYTable";
import {
  ProductionContext,
  ProductionVariant,
  useGrouping,
  useGroupOption,
  useGroups,
  useHideEmptyMaterialRows,
  useIsTransposed,
  useMeasureOption,
  useMixes,
  usePeriodMaterialSummaries,
  usePeriodOption,
  useSteelGradesOption,
} from "./ProductionContext";
import {
  ChefGroup,
  MaterialRead,
  MixMetadata,
  MixSummary,
  NameBasedPeriodMaterialSummary,
  ProductGroup,
  ProductionMixResultRead,
} from "src/store/api/generatedApi";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { SystemStyleObject, Theme } from "@mui/system";
import { PlanMixes } from "./productionContent";

interface Props {
  productGroups: ProductGroup[] | null;
  chemistryGroups: ChefGroup[];
  mixResults: ProductionMixResultRead[];
  productGroupResults: ProductionMixResultRead[];
  mixSummaries: MixSummary[];
  periodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  materials: MaterialRead[];
  period: number;
  mixMetadata: MixMetadata[];
  variant?: ProductionVariant;
  hideEmptyMaterialRows?: boolean;
  mixColumnWidthPx?: number;
  sx?: SystemStyleObject<Theme>;
}

export const ProductionTable = ({
  productGroups,
  chemistryGroups,
  mixResults,
  productGroupResults,
  mixSummaries,
  periodMaterialSummaries,
  materials,
  period,
  mixMetadata,
  variant,
  hideEmptyMaterialRows,
  mixColumnWidthPx,
  sx,
}: Props) => {
  return (
    <Stack gap={2} sx={[{ height: "100%" }, ...(sx ? [sx] : [])]}>
      <ProductionContext
        period={period}
        productGroups={productGroups}
        chemistryGroups={chemistryGroups}
        mixResults={mixResults}
        productGroupResults={productGroupResults}
        mixSummaries={mixSummaries}
        planPeriodMaterialSummaries={periodMaterialSummaries}
        materials={materials}
        hasProductGroups
        mixMetadata={mixMetadata}
        variant={variant}
        hideEmptyMaterialRows={hideEmptyMaterialRows}
        mixColumnWidthPx={mixColumnWidthPx}
      >
        <Table
          key={period}
          materials={materials}
          chemistryGroups={chemistryGroups}
        />
      </ProductionContext>
    </Stack>
  );
};

const materialHasMass = (materialId: number, mixes: PlanMixes): boolean =>
  Object.values(mixes).some((groupMixes) =>
    Object.values(groupMixes).some(
      (mix) => (mix.materials[materialId] ?? 0) > 0
    )
  );

const sx = {
  "& .MuiDataGrid-cell": {
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& .MuiDataGrid-columnHeader": {
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    height: "100%",
  },
  "& .group--header": {
    px: 1,
    py: 0.5,
  },
};

const Table = ({
  materials,
  chemistryGroups,
}: {
  materials: MaterialRead[];
  chemistryGroups: ChefGroup[];
}) => {
  const isTransposed = useIsTransposed();

  const group = useGroupOption();
  const measure = useMeasureOption();
  const period = usePeriodOption();
  const steelGrades = useSteelGradesOption();
  const planGroups = useGroups();
  const grouping = useGrouping();
  const periodMaterialSummaries = usePeriodMaterialSummaries();
  const planMixes = useMixes();
  const hideEmptyMaterialRows = useHideEmptyMaterialRows();

  const visibleMaterials = hideEmptyMaterialRows
    ? materials.filter((material) => materialHasMass(material.id, planMixes))
    : materials;

  if (isTransposed) {
    return (
      <ScrapXHeatYTable
        materials={visibleMaterials}
        group={group}
        period={period}
        measure={measure}
        steelGrades={steelGrades}
        planGroups={planGroups}
        grouping={grouping}
        chemistryGroups={typeSafeObjectFromEntries(
          chemistryGroups.map((chemistryGroup) => [
            chemistryGroup.id!,
            chemistryGroup,
          ])
        )}
        periodMaterialSummaries={periodMaterialSummaries}
        planMixes={planMixes}
        sx={sx}
      />
    );
  } else {
    return (
      <HeatXScrapYTable
        materials={visibleMaterials}
        measure={measure}
        planGroups={planGroups}
        planMixes={planMixes}
        grouping={grouping}
        steelGrades={steelGrades}
        chemistryGroups={typeSafeObjectFromEntries(
          chemistryGroups.map((chemistryGroup) => [
            chemistryGroup.id!,
            chemistryGroup,
          ])
        )}
        group={group}
        period={period}
        sx={sx}
      />
    );
  }
};
